import { ForecastSeriesCompletedWithoutMeasure } from '@api-response-types/forecast-series.response';
import { Resolution } from '@api-types/enums/resolution.enum';
import { Timespan } from '@api-types/types/timespan.type';
import { unixTimeToDesiredResolutionUnixTime } from '@libs/forecast-types/src/lib/helper/date-resolution';

const getUnixTime = (date: number, resolution?: Resolution): number =>
  resolution ? unixTimeToDesiredResolutionUnixTime(date, resolution) : date;

export const getMinAndMaxDate = (
  series: ForecastSeriesCompletedWithoutMeasure,
  resolution?: Resolution,
): [number, number] => {
  if (!series) {
    return [0, 0];
  }
  const minDate = getUnixTime(series.values.data[0][0], resolution);
  const predictionLength = series.predictions.data.length - 1;
  const maxDate = getUnixTime(
    series.predictions.data[predictionLength][0],
    resolution,
  );
  return [minDate, maxDate];
};

export const getPlotbandStartDate = (
  series: ForecastSeriesCompletedWithoutMeasure,
  resolution?: Resolution,
): number => {
  if (!series) {
    return 0;
  }
  const initialFirstPredictionDate = getUnixTime(
    series.predictions.data[series.initialForecastIndex][0],
    resolution,
  );
  const initialLastActualDate = getUnixTime(
    series.predictions.data[series.initialForecastIndex - 1][0],
    resolution,
  );
  if (!initialFirstPredictionDate || !initialLastActualDate) {
    throw new Error(
      'initialFirstPredictionDate or initialLastActualDate is undefined',
    );
  }
  return (initialFirstPredictionDate + initialLastActualDate) / 2;
};

export const calculatePlotbandStartDateAndSeriesMaxDate = (
  timespan: Timespan,
  resolution: Resolution,
): { plotBandStartDate: number; seriesMaxDate: number } => {
  const futureStartDate = new Date(timespan.future_start_date).getTime();
  const dateBeforeFirstPredictionDate = new Date(futureStartDate);
  switch (resolution) {
    case Resolution.Daily:
      dateBeforeFirstPredictionDate.setDate(
        dateBeforeFirstPredictionDate.getDate() - 1,
      );
      break;
    case Resolution.Weekly:
      dateBeforeFirstPredictionDate.setDate(
        dateBeforeFirstPredictionDate.getDate() - 7,
      );
      break;
    case Resolution.Yearly:
      dateBeforeFirstPredictionDate.setFullYear(
        dateBeforeFirstPredictionDate.getFullYear() - 1,
      );
      break;
    default: // Default: Monthly
      dateBeforeFirstPredictionDate.setMonth(
        dateBeforeFirstPredictionDate.getMonth() - 1,
      );
  }

  const plotBandStartDate =
    (futureStartDate + dateBeforeFirstPredictionDate.getTime()) / 2;
  const seriesMaxDate = new Date(timespan.max_date).getTime();

  return { plotBandStartDate, seriesMaxDate };
};
